import { IconDownload } from '@tabler/icons-react';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from "react-router-dom";
import axios from 'axios';
import { toast } from 'react-toastify';

export default function SingleSalarySlip() {
    const { emp_id, id } = useParams();
    const [salarySlipData, setSalarySlipData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    // Access permissions array from Redux store and userId from localStorage
    const permissions = useSelector((state) => state.emp.permissions);
    const userId = localStorage.getItem("userId");

    const pagePermissions = ["showsalaryslipID", "downloadsalaryslipID"];

    const accessToken = localStorage.getItem('accessToken');

    const canViewSalarySlip = permissions.includes("showsalaryslipID") || String(emp_id) === String(userId);

    useEffect(() => {
        const fetchSalarySlip = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API}/api/attendance/salary-slip-list/${id}/`,
                    {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch salary slip data');
                }

                const result = await response.json();
                if (result.success) {
                    setSalarySlipData(result.data);
                } else {
                    toast.error('Failed to load salary slip data');
                }
            } catch (error) {
                console.error('Error fetching salary slip:', error);
                toast.error('Failed to load salary slip data');
            } finally {
                setLoading(false);
            }
        };

        if (canViewSalarySlip) {
            fetchSalarySlip();
        } else {
            setLoading(false);
        }
    }, [id, canViewSalarySlip, accessToken]);

    const handleDownload = async () => {
        try {
            setDownloading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_API}/api/attendance/download-salary-slip-pdf/${id}/`,
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    responseType: 'blob'
                }
            );

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            const fileName = `salary_slip_${salarySlipData?.employee?.name}_${salarySlipData?.pay_month}.pdf`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success('Salary slip downloaded successfully');
        } catch (error) {
            console.error('Error downloading salary slip:', error);
            toast.error('Failed to download salary slip');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!canViewSalarySlip) {
        return <div>No permission to view salary slip</div>;
    }

    if (!salarySlipData) {
        return <div>No salary slip data available</div>;
    }

    const formattedData = {
        employeeName: salarySlipData.employee.name,
        designation: salarySlipData.employee.job_position.job_title,
        month: new Date(salarySlipData.pay_month).toLocaleString('default', {
            month: 'long',
            year: 'numeric'
        }),
        salaryAmount: `${salarySlipData.basic_salary.toLocaleString()} INR`,
        deductions: `${salarySlipData.deductions.toLocaleString()} INR`,
        pfDeduction: "N/A",
        netPayable: `${salarySlipData.total_earnings.toLocaleString()} INR`,
        status: "Paid",
        issuedDate: new Date(salarySlipData.salary_given_date).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        }),
    };

    return (
        <div className="container-fluid px-2 px-md-5 mb-5 mt-4 d-flex flex-column justify-content-center align-items-center">
            <div className="a4-container">
                <div className="a4-page">
                    <div
                        className="content-wrapper"
                        style={{
                            backgroundImage: "url('/assets/certificate.jpg')",
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                        }}
                    >
                        <div className="content d-flex flex-column justify-content-start">
                            <div className="mb-md-4">
                                <p className="mb-1"><span className='fw-medium'>Employee Name: </span>{formattedData.employeeName}</p>
                                <p className="mb-1"><span className='fw-medium'>Designation: </span>{formattedData.designation}</p>
                                <p className="mb-1"><span className='fw-medium'>Month: </span>{formattedData.month}</p>
                            </div>

                            <table className='sliptable table w-100'>
                                <thead className="table-light rounded-0">
                                    <tr>
                                        <th className='rounded-0'>Salary Amount</th>
                                        <th>Deduction</th>
                                        <th>PF Deduction</th>
                                        <th>Net Payable</th>
                                        <th className='rounded-0'>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{formattedData.salaryAmount}</td>
                                        <td>{formattedData.deductions}</td>
                                        <td>{formattedData.pfDeduction}</td>
                                        <td>{formattedData.netPayable}</td>
                                        <td className='text-success'>{formattedData.status}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <p className="mb-1 mt-md-3 mt-1">
                                Salary Issued Date: <span className='ms-2'>{formattedData.issuedDate}</span>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {(permissions.includes("downloadsalaryslipID") || String(emp_id) === String(userId)) && (
                <div className='w-100 d-flex justify-content-center mt-4'>
                    <button
                        className='btn btn-success'
                        onClick={handleDownload}
                        disabled={downloading}
                    >
                        {downloading ? 'Downloading...' : 'Download'}
                        <IconDownload stroke={2} size={20} className='ms-1' />
                    </button>
                </div>
            )}

            <style jsx>{`
                .a4-container {
                    width: 100%;
                    max-width: 694.5px;
                    max-height: 981.5px;
                    margin: 0 auto;
                    position: relative;
                }

                .a4-page {
                    width: 100%;
                    padding-top: 141.4%;
                    position: relative;
                    box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
                    box-sizing: border-box;
                    max-height: 981.5px;
                }

                .content-wrapper {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                }

                .content {
                    position: absolute;
                    top: 35%;
                    left: 0;
                    width: 100%;
                    transform: translateY(-50%);
                    padding: 30px;
                }

                th, td {
                    vertical-align: middle !important;
                    font-size: 14px;
                }

                p {
                    font-size: 14px;
                }

                .sliptable {
                    width: calc(100% - 2rem);
                    table-layout: fixed;
                    margin-top: 50px;
                }

                .sliptable th {
                    font-weight: 500;
                    background-color: #d2972995;
                    color: black;
                    font-size: 14px;
                    white-space: nowrap;
                }

                .sliptable td {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                @media (max-width: 768px) {
                    .a4-container {
                        max-width: 100%;
                        padding: 0 10px;
                    }

                    .sliptable {
                        width: calc(100% - 1rem);
                        margin-top: 10px;
                    }
                    .sliptable th {
                        font-size: 12px !important;
                    }
                    th, td {
                        padding: 6px !important;
                        font-size: 10px;
                    }

                    p {
                        font-size: 12px;
                    }
                }

                @media (max-width: 576px) {
                    .content {
                        padding: 10px;
                    }

                    .sliptable {
                        width: calc(100% - 1rem);
                        margin-bottom: 5px;
                        margin-top: 25px;
                    }
                    .sliptable th {
                        font-size: 7px !important;
                    }
                    th, td {
                        padding: 4px !important;
                        font-size: 8px;
                    }

                    p {
                        font-size: 10px;
                    }
                }
            `}</style>
        </div>
    );
}